import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import AuthContext from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import {
  getRechartsLegendPieProps,
  getRechartsTooltipProps,
  getStatisticsTotalsRowSx,
  viewRootTextPrimarySx,
} from '../../util/chartTheme';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import useStatisticJob from '../../hooks/useStatisticJob';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_BON_REVENUE } from '../../constants/statisticTypes';

function formatEuroCents(cents) {
  const euros = Number(cents || 0) / 100;
  return (
    euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
  );
}
function formatEuro(euros) {
  return (
    Number(euros || 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  );
}
function formatPercent(fraction) {
  const val = Number(fraction || 0) * 100;
  return val.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}

// Count helper for the "aktuelle Daten" dropdown labels.
const countCsv = raw => String(raw || '').split(',').filter(Boolean).length;

const BonRevenue = () => {
  const theme = useTheme();
  const totalsRowSx = useMemo(() => getStatisticsTotalsRowSx(theme), [theme]);

  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const pieChartColors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.light,
      theme.palette.secondary.light,
      theme.palette.success.light,
      theme.palette.warning.light,
    ],
    [theme]
  );

  const authCtx = useContext(AuthContext);
  if (!authCtx.checkRight('statistic:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const { getBranches, getAllEntertainmentsOfBranches } = useBranch();

  const [startDate, setStartDate] = useState(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [branchOptions, setBranchOptions] = useState([]);
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [entertainments, setEntertainments] = useState([]);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [includeOnline, setIncludeOnline] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState({});

  // Seed with today's range so the view auto-loads the most recent cached
  // entry (if any) on mount. Empty branch/entertainment arrays mean "all"
  // on the backend, which keeps the default cache key stable regardless
  // of how many branches/entertainments exist in the system.
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
    branchIds: [],
    entertainmentNames: [],
    includeDetails: false,
    includeOnline: false,
  });
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const toggleBranch = branchName => {
    setExpandedBranches(prev => ({ ...prev, [branchName]: !prev[branchName] }));
  };

  useEffect(() => {
    const f = async () => {
      const loaded = await getBranches();
      const opts = (loaded || []).map(b => ({ label: b.locationName, id: b._id }));
      setBranchOptions(opts);
      setBranches(opts);
      const allEnterts = await getAllEntertainmentsOfBranches((loaded || []).map(b => b._id));
      const entOpts = (allEnterts || []).map(e => ({
        label: typeof e === 'string' ? e : e.label || e.longName || '',
      }));
      setEntertainmentOptions(entOpts);
      setEntertainments(entOpts);
      setOptionsLoading(false);
    };
    f();
  }, []);

  useEffect(() => {
    const f = async () => {
      if (!branches.length) return;
      const branchIds = branches.map(b => b.id);
      const allEnterts = await getAllEntertainmentsOfBranches(branchIds);
      const entOpts = (allEnterts || []).map(e => ({
        label: typeof e === 'string' ? e : e.label || e.longName || '',
      }));
      setEntertainmentOptions(entOpts);
      setEntertainments(entOpts);
    };
    f();
  }, [branches]);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branches: appliedFilter.branchIds.join(','),
      entertainments: appliedFilter.entertainmentNames.join(','),
      includeDetails: appliedFilter.includeDetails ? '1' : '0',
      includeOnline: appliedFilter.includeOnline ? '1' : '0',
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    refresh,
    loadCacheEntry,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_BON_REVENUE,
    endpoint: '/api/v1/bon/statistics',
    filterParams,
    autoFetchOnMount: true,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_BON_REVENUE,
  });

  const handleApply = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || e.isBefore(s, 'day')) return;
      // "All selected" and "none selected" are semantically equivalent on
      // the backend ("no filter"). Normalize to an empty array in both
      // cases so the cache key stays stable and users don't accidentally
      // create a second cache entry just by toggling the autocomplete.
      const allBranchesSelected =
        branchOptions.length > 0 && branches.length === branchOptions.length;
      const allEntsSelected =
        entertainmentOptions.length > 0 && entertainments.length === entertainmentOptions.length;
      const branchIds = allBranchesSelected ? [] : [...branches.map(b => b.id)].sort();
      const entertainmentNames = allEntsSelected
        ? []
        : [...entertainments.map(en => en.label).filter(Boolean)].sort();
      const snapshot = {
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        branchIds,
        entertainmentNames,
        includeDetails,
        includeOnline,
      };
      setAppliedFilter(snapshot);
      setSelectedEntryId(null);
    },
    [
      startDate,
      endDate,
      branches,
      branchOptions,
      entertainments,
      entertainmentOptions,
      includeDetails,
      includeOnline,
    ]
  );

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId || null);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const data = payload;

  const byBranchRows = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    (data.revenueByBranchEntertainment || []).forEach(r => {
      const key = r.branchId || '—';
      if (!map.has(key)) map.set(key, { branchName: r.branchName || '—', total: 0, ents: {} });
      const row = map.get(key);
      row.total += r.revenueCents;
      const entName = r.entertainmentName || '—';
      row.ents[entName] = (row.ents[entName] || 0) + r.revenueCents;
    });
    const rows = Array.from(map.values()).map(row => {
      const entsArr = Object.entries(row.ents)
        .map(([name, revenueCents]) => ({ name, revenueCents }))
        .sort((a, b) => b.revenueCents - a.revenueCents);
      return { branchName: row.branchName, total: row.total, ents: entsArr };
    });
    return rows.sort((a, b) => b.total - a.total);
  }, [data]);

  const pieEntertainment = useMemo(() => {
    if (!data) return [];
    return (data.pieByEntertainment || [])
      .map(p => ({ name: p.entertainmentName || '—', value: (p.revenueCents || 0) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const pieSuperGroup = useMemo(() => {
    if (!data) return [];
    return (data.pieBySuperGroup || [])
      .map(p => ({ name: p.superGroup || '—', value: (p.revenueCents || 0) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const grandTotalCents = useMemo(
    () => byBranchRows.reduce((sum, r) => sum + (r.total || 0), 0),
    [byBranchRows]
  );
  const totalEntertainmentEuros = useMemo(
    () => pieEntertainment.reduce((sum, r) => sum + (r.value || 0), 0),
    [pieEntertainment]
  );
  const totalSuperGroupEuros = useMemo(
    () => pieSuperGroup.reduce((sum, r) => sum + (r.value || 0), 0),
    [pieSuperGroup]
  );

  // Whether the filter snapshot attached to the current payload (or a loaded
  // cache entry) has `includeDetails = true`. Needed because the live
  // `includeDetails` state reflects the unapplied filter and can differ from
  // the data currently displayed (e.g. after picking an older cache entry).
  const payloadIncludeDetails = useMemo(() => {
    if (!data) return false;
    return Array.isArray(data.superGroups) && data.superGroups.length > 0;
  }, [data]);

  const applyIsDisabled =
    optionsLoading || !startDate || !endDate || endDate.isBefore(startDate, 'day');
  const refreshIsDisabled =
    status === 'fetching' || status === 'pending' || status === 'running';

  // Non-hidden entries are keyed by the same `filterParams` we build for the
  // job runner. We want short, contextual labels in the dropdown so users see
  // at a glance *what* the entry was computed for without exposing raw ids.
  const shellFilterParamConfig = useMemo(
    () => ({
      branchScope: { hidden: true },
      branches: {
        label: 'Standorte',
        hidden: v => countCsv(v) === 0,
        format: v => String(countCsv(v)),
      },
      entertainments: {
        label: 'Entertainments',
        hidden: v => countCsv(v) === 0,
        format: v => String(countCsv(v)),
      },
      includeDetails: {
        label: 'Details',
        hidden: v => v !== '1',
        format: () => 'ja',
      },
      includeOnline: {
        label: 'Online',
        hidden: v => v !== '1',
        format: () => 'ja',
      },
    }),
    []
  );

  // Custom renderTags that always limits to `max` chips + a "+N" counter,
  // regardless of focus. MUI's built-in `limitTags` only applies when the
  // autocomplete is unfocused — on click it renders every chip, which
  // blows up the filter row height with 16+ branches pre-selected. This
  // keeps the row height stable and the floating label aligned.
  // Keep the Autocomplete's inner input row on a single line so the
  // field height stays constant (~56px) regardless of how many chips
  // are rendered. Without this, MUI wraps chips + counter + caret onto
  // a second row as soon as the combined width exceeds the field.
  const singleLineAutocompleteSx = {
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '30px !important',
      flex: '1 1 30px',
    },
  };

  const renderLimitedTags = max => (value, getTagProps) => {
    const displayed = value.slice(0, max);
    const remaining = value.length - displayed.length;
    return (
      <>
        {displayed.map((option, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return (
            <Chip
              {...chipProps}
              key={key}
              label={(option && option.label) || ''}
              size='small'
            />
          );
        })}
        {remaining > 0 && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ ml: 0.5, alignSelf: 'center' }}
          >
            +{remaining}
          </Typography>
        )}
      </>
    );
  };

  const branchesFilterNode = (
    <Autocomplete
      multiple
      id='bon-revenue-branches'
      options={branchOptions}
      getOptionKey={option => option.id}
      getOptionLabel={option => (option && option.label) || ''}
      value={branches}
      onChange={(_e, val) => setBranches(val)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderTags={renderLimitedTags(2)}
      sx={singleLineAutocompleteSx}
      renderInput={params => <TextField {...params} label='Standorte' variant='outlined' />}
    />
  );

  const entertainmentsFilterNode = (
    <Autocomplete
      multiple
      id='bon-revenue-entertainments'
      options={entertainmentOptions}
      getOptionLabel={option => (option && option.label) || ''}
      value={entertainments}
      onChange={(_e, val) => setEntertainments(val)}
      isOptionEqualToValue={(option, value) => option.label === value.label}
      renderTags={renderLimitedTags(2)}
      sx={singleLineAutocompleteSx}
      renderInput={params => <TextField {...params} label='Entertainments' variant='outlined' />}
    />
  );

  const togglesActionsNode = (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        flexWrap: 'wrap',
        rowGap: 0.5,
        alignItems: 'center',
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={includeDetails}
            onChange={e => setIncludeDetails(e.target.checked)}
            size='small'
          />
        }
        label='Details'
      />
      <FormControlLabel
        control={
          <Switch
            checked={includeOnline}
            onChange={e => setIncludeOnline(e.target.checked)}
            size='small'
          />
        }
        label='Online'
      />
    </Stack>
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      extraFilters={[branchesFilterNode, entertainmentsFilterNode]}
      onApply={handleApply}
      applyDisabled={applyIsDisabled}
      applyLabel='Auswerten'
      applyExtraActions={togglesActionsNode}
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refresh}
      refreshDisabled={refreshIsDisabled}
      filterParamConfig={shellFilterParamConfig}
      hasPayload={payload != null}
      externallyLoading={false}
    >
      {data && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant='h6'>Umsatz nach Standort und Entertainment</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}></TableCell>
                  <TableCell>Standort / Entertainment</TableCell>
                  <TableCell align='right'>Umsatz</TableCell>
                  {payloadIncludeDetails &&
                    (data?.superGroups || []).map((sg, idx) => (
                      <TableCell key={idx} align='right'>
                        {sg.name || '—'}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {byBranchRows.map((row, idx) => (
                  <React.Fragment key={idx}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          aria-label='expand row'
                          size='small'
                          onClick={() => toggleBranch(row.branchName)}
                        >
                          {expandedBranches[row.branchName] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.branchName}</TableCell>
                      <TableCell align='right'>
                        {formatEuroCents(row.total)}
                        {grandTotalCents > 0 && (
                          <Typography
                            component='span'
                            variant='caption'
                            color='text.secondary'
                            sx={{ ml: 1 }}
                          >
                            {formatPercent(row.total / grandTotalCents)}
                          </Typography>
                        )}
                      </TableCell>
                      {payloadIncludeDetails &&
                        (data?.superGroups || []).map((sg, sIdx) => {
                          const val = (data?.byBranchSuperGroup || [])
                            .filter(
                              r =>
                                r.branchName === row.branchName &&
                                (r.superGroup || '—') === (sg.name || '—')
                            )
                            .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                          return (
                            <TableCell key={sIdx} align='right'>
                              {formatEuroCents(val)}
                            </TableCell>
                          );
                        })}
                    </TableRow>

                    {row.ents.map((e, eIdx) => (
                      <TableRow
                        key={eIdx}
                        style={{ display: expandedBranches[row.branchName] ? '' : 'none' }}
                      >
                        <TableCell></TableCell>
                        <TableCell sx={{ pl: 3 }}>{e.name}</TableCell>
                        <TableCell align='right'>
                          {formatEuroCents(e.revenueCents)}
                          {row.total > 0 && (
                            <Typography
                              component='span'
                              variant='caption'
                              color='text.secondary'
                              sx={{ ml: 1 }}
                            >
                              {formatPercent(e.revenueCents / row.total)}
                            </Typography>
                          )}
                        </TableCell>
                        {payloadIncludeDetails &&
                          (data?.superGroups || []).map((sg, sIdx) => {
                            const val = (data?.byBranchEntertainmentSuperGroup || [])
                              .filter(
                                r =>
                                  r.branchName === row.branchName &&
                                  r.entertainmentName === e.name &&
                                  (r.superGroup || '—') === (sg.name || '—')
                              )
                              .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                            return (
                              <TableCell key={sIdx} align='right'>
                                {formatEuroCents(val)}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                <TableRow sx={totalsRowSx}>
                  <TableCell sx={{ fontWeight: 600 }}>Gesamt</TableCell>
                  <TableCell></TableCell>
                  <TableCell align='right'>{formatEuroCents(grandTotalCents)}</TableCell>
                  {payloadIncludeDetails &&
                    (data?.superGroups || []).map((sg, sIdx) => {
                      const val = (data?.byBranchSuperGroup || [])
                        .filter(r => (r.superGroup || '—') === (sg.name || '—'))
                        .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                      return (
                        <TableCell key={sIdx} align='right'>
                          {formatEuroCents(val)}
                        </TableCell>
                      );
                    })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Grid2 container spacing={2}>
            <Grid2 xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={0}>
                <Typography variant='h6' sx={{ mb: 1 }}>
                  Umsatz nach Entertainment
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieEntertainment}
                        dataKey='value'
                        nameKey='name'
                        outerRadius={120}
                        isAnimationActive={false}
                      >
                        {pieEntertainment.map((entry, index) => (
                          <Cell key={`cell-e-${index}`} fill={pieChartColors[index % pieChartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...chartTooltipProps}
                        formatter={value =>
                          `${formatEuro(value)} (${formatPercent(
                            value / (totalEntertainmentEuros || 1)
                          )})`
                        }
                      />
                      <Legend {...chartLegendPieProps} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Entertainment</TableCell>
                        <TableCell align='right'>Umsatz</TableCell>
                        <TableCell align='right'>Anteil</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pieEntertainment.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.name || '—'}</TableCell>
                          <TableCell align='right'>{formatEuro(row.value)}</TableCell>
                          <TableCell align='right'>
                            <Typography variant='caption' color='text.secondary'>
                              {formatPercent((row.value || 0) / (totalEntertainmentEuros || 1))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={0}>
                <Typography variant='h6' sx={{ mb: 1 }}>
                  Umsatz nach Sammelwarengruppe
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieSuperGroup}
                        dataKey='value'
                        nameKey='name'
                        outerRadius={120}
                        isAnimationActive={false}
                      >
                        {pieSuperGroup.map((entry, index) => (
                          <Cell key={`cell-s-${index}`} fill={pieChartColors[index % pieChartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        {...chartTooltipProps}
                        formatter={value =>
                          `${formatEuro(value)} (${formatPercent(
                            value / (totalSuperGroupEuros || 1)
                          )})`
                        }
                      />
                      <Legend {...chartLegendPieProps} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Sammelwarengruppe</TableCell>
                        <TableCell align='right'>Umsatz</TableCell>
                        <TableCell align='right'>Anteil</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pieSuperGroup.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.name || '—'}</TableCell>
                          <TableCell align='right'>{formatEuro(row.value)}</TableCell>
                          <TableCell align='right'>
                            <Typography variant='caption' color='text.secondary'>
                              {formatPercent((row.value || 0) / (totalSuperGroupEuros || 1))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid2>
          </Grid2>
        </>
      )}
    </StatisticViewShell>
    </Box>
  );
};

export default BonRevenue;
